import { Add, Delete } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useNotify } from 'react-admin';
import { ClinicConfig, CptCodeConfig } from '../../../interfaces/billing.rpm';
import { EMPTY_CPT_CODE_FIELDS_ERROR } from '../common/constants';
import './clinic-configuration-form.css';

export type AddClinicProps = {
  clinic?: ClinicConfig;
  onChangeFormData: (clinicConfig: ClinicConfig, editing: boolean) => void;
};

export const ClinicConfigurationForm: React.FC<AddClinicProps> = ({ onChangeFormData, clinic }) => {
  const [payerId, setPayerId] = useState(clinic ? clinic.payerId : '');
  const [clinicId, setClinicId] = useState(clinic ? clinic.clinicId : '');
  const [placeOfService, setPlaceOfService] = useState(clinic ? clinic.placeOfService : '');
  const [configurationEnabled, setConfigurationEnabled] = useState(
    clinic ? clinic.rpmEnabled : false,
  );
  const [cptCodesFields, setCptCodesFields] = useState<CptCodeConfig[]>(
    clinic
      ? Object.entries(clinic.cptCodesConfig).map(([key, value]) => ({
          ...value,
          code: value.code ?? key,
        }))
      : [],
  );

  const notify = useNotify();

  const handleFormChange = () => {
    if (!payerId || !clinicId || !placeOfService) {
      notify(EMPTY_CPT_CODE_FIELDS_ERROR, 'error', {
        autoHideDuration: 30,
      });
      return;
    }

    const cptCodes: Record<string, CptCodeConfig> = cptCodesFields.reduce((acc, item) => {
      acc[item.code!] = item;
      delete acc[item.code!].code; // not necessary to send as attribute to the server
      return acc;
    }, {} as Record<string, CptCodeConfig>);

    const clinicData: ClinicConfig = {
      ...(clinic && { id: clinic.id }),
      payerId,
      clinicId: Number(clinicId),
      placeOfService: placeOfService,
      rpmEnabled: configurationEnabled,
      cptCodesConfig: cptCodes,
    };

    onChangeFormData(clinicData, clinic ? true : false);
  };

  const handleAddCptCode = () => {
    setCptCodesFields([
      ...cptCodesFields,
      { code: '', enabled: true, max_claims_per_cycle: 1, pos: 11 },
    ]);
  };

  const handleRemoveCptCode = (index: number) => {
    setCptCodesFields((prevFields) => prevFields.filter((_, i) => i !== index));
  };

  const handleCptCodeFieldChange = (elementIndex: number, fieldName: string, value: any) => {
    setCptCodesFields(
      cptCodesFields.map((field, index) =>
        index === elementIndex ? { ...field, [fieldName]: value } : field,
      ),
    );
  };

  const renderCptCodeSection = () => {
    return (
      <Paper elevation={3} className='clinic-configuration cpt-code-section'>
        <Typography variant='subtitle2' className='clinic-configuration cpt-code-section-legend'>
          CPT CODES
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            className='clinic-configuration add-cpt-code-btn'
            variant='contained'
            color='primary'
            onClick={handleAddCptCode}>
            <Add />
          </Button>
        </Box>

        {cptCodesFields.map((cptCode, index) => {
          return (
            <Box display='flex' gap={2} alignItems='center'>
              <TextField
                label='Code'
                fullWidth
                margin='normal'
                value={cptCode.code}
                onChange={(e) => handleCptCodeFieldChange(index, 'code', e.target.value)}
              />

              <TextField
                label='Max Claim per Cycle'
                fullWidth
                margin='normal'
                value={cptCode.max_claims_per_cycle}
                onChange={(e) =>
                  handleCptCodeFieldChange(index, 'max_claims_per_cycle', e.target.value)
                }
              />

              <TextField
                label='POS'
                fullWidth
                margin='normal'
                value={cptCode.pos}
                onChange={(e) => handleCptCodeFieldChange(index, 'pos', e.target.value)}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={cptCode.enabled}
                    value={cptCode.enabled}
                    onChange={(e) => handleCptCodeFieldChange(index, 'enabled', e.target.checked)}
                  />
                }
                label='Enabled'
              />

              <IconButton onClick={() => handleRemoveCptCode(index)} color='error'>
                <Delete />
              </IconButton>
            </Box>
          );
        })}
      </Paper>
    );
  };

  return (
    <Container className='clinic-configuration add-clinic-main-container'>
      {!clinic && <Typography variant='subtitle1'>NEW CLINIC CONFIGURATION</Typography>}

      <Box display='flex' gap={2} alignItems='center'>
        <TextField
          label='Payer ID'
          fullWidth
          margin='normal'
          value={payerId}
          disabled={clinic ? true : false}
          onChange={(e) => setPayerId(e.target.value)}
        />

        <TextField
          label='Clinic ID'
          fullWidth
          margin='normal'
          value={clinicId}
          disabled={clinic ? true : false}
          onChange={(e) => setClinicId(e.target.value)}
        />

        <TextField
          label='Place of Service'
          fullWidth
          margin='normal'
          value={placeOfService}
          onChange={(e) => setPlaceOfService(e.target.value)}
        />
      </Box>

      <FormControlLabel
        control={
          <Checkbox
            value={configurationEnabled}
            checked={configurationEnabled}
            onChange={(e) => setConfigurationEnabled(e.target.checked)}
          />
        }
        label='RPM Enabled'
      />

      {renderCptCodeSection()}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 2, marginTop: 2 }}>
        <Button variant='contained' color='primary' onClick={handleFormChange}>
          {!clinic ? 'Add Configuration' : 'Edit Configuration'}
        </Button>
      </Box>
    </Container>
  );
};
